let panelSequence = 0
const CONTROL_ATTRIBUTES = ["role", "aria-expanded", "aria-controls"]

// 余白のない画面で、参照した段落の直後に一つずつ脚注を展開する。
export default class InlineFootnotes {
  constructor(element) {
    this.element = element
    this.references = new Map()
    this.originalAttributes = new Map()
    this.footnotes = new Set()
    element.addEventListener("click", this.onClick)
    element.addEventListener("keydown", this.onKeydown)
  }

  update(notes) {
    const references = new Map(notes.flatMap(entry => entry.references.map(reference => [reference, entry])))
    for (const reference of this.references.keys()) {
      if (!references.has(reference)) this.restoreReference(reference)
    }
    this.references = references
    const footnotes = new Set(notes.map(entry => entry.footnotes))
    for (const container of this.footnotes) {
      if (!footnotes.has(container)) container.classList.remove("inline-footnotes-source")
    }
    this.footnotes = footnotes
    for (const container of footnotes) container.classList.add("inline-footnotes-source")
    for (const reference of references.keys()) {
      if (!this.originalAttributes.has(reference)) {
        this.originalAttributes.set(reference, CONTROL_ATTRIBUTES.map(name => [name, reference.getAttribute(name)]))
      }
      reference.setAttribute("role", "button")
      reference.setAttribute("aria-expanded", String(reference === this.active?.reference))
    }
    if (!this.active) return

    if (references.get(this.active.reference)?.note !== this.active.originNote || !this.active.panel.isConnected || !this.active.note.isConnected) {
      this.close()
    } else if (this.active.source !== this.active.note.innerHTML) {
      this.updateContent()
    }
  }

  disable() {
    this.close(this.active?.panel.contains(document.activeElement))
    for (const reference of this.references.keys()) this.restoreReference(reference)
    for (const container of this.footnotes) container.classList.remove("inline-footnotes-source")
    this.references.clear()
    this.footnotes.clear()
  }

  disconnect() {
    this.disable()
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("keydown", this.onKeydown)
  }

  restoreReference(reference) {
    for (const [name, value] of this.originalAttributes.get(reference) || []) {
      if (value === null) reference.removeAttribute(name)
      else reference.setAttribute(name, value)
    }
    this.originalAttributes.delete(reference)
  }

  onClick = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    const reference = event.target.closest("a.footnote")
    if (this.followNestedReference(reference)) {
      event.preventDefault()
      return
    }
    if (!this.references.has(reference)) return

    event.preventDefault()
    this.toggle(reference)
  }

  onKeydown = (event) => {
    if (event.key === "Escape" && this.active) {
      event.preventDefault()
      this.close(true)
    } else if (event.key === " " && (this.references.has(event.target) || this.active?.panel.contains(event.target.closest("a.footnote")))) {
      // Enter はリンクの標準 click を利用し、button として扱う Space も受け付ける。
      event.preventDefault()
      if (!this.followNestedReference(event.target)) this.toggle(event.target)
    }
  }

  toggle(reference) {
    if (this.active?.reference === reference) {
      this.close(true)
      return
    }
    this.close()
    const { note } = this.references.get(reference)
    const panel = document.createElement("aside")
    panel.className = "inline-footnote"
    panel.id = `inline-footnote-${++panelSequence}`
    panel.setAttribute("aria-label", `脚注 ${reference.textContent.trim()}`)
    const heading = document.createElement("div")
    heading.className = "inline-footnote-heading"
    const label = document.createElement("span")
    const backButton = document.createElement("button")
    backButton.type = "button"
    backButton.textContent = "前の脚注に戻る"
    backButton.addEventListener("click", () => {
      Object.assign(this.active, this.active.history.pop())
      this.updateContent()
      this.active.content.focus({ preventScroll: true })
    })
    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.textContent = "閉じる"
    closeButton.addEventListener("click", () => this.close(true))
    heading.append(label, closeButton)
    const content = document.createElement("div")
    content.className = "inline-footnote-body"
    content.tabIndex = -1
    panel.append(heading, backButton, content)
    this.active = { reference, note, originNote: note, panel, content, label, backButton, number: reference.textContent.trim(), history: [] }
    this.updateContent()

    // 表の中へ aside を挿入すると不正な構造になるため、表の参照は表全体の直後に置く。
    const block = reference.closest("table") || reference.closest("p, li, h1, h2, h3, h4, h5, h6, blockquote") || reference.closest("sup")
    if (block.matches("li")) block.append(panel)
    else block.after(panel)
    reference.setAttribute("aria-expanded", "true")
    reference.setAttribute("aria-controls", panel.id)
  }

  updateContent() {
    const { note, content } = this.active
    const copy = note.cloneNode(true)
    // 印刷・JavaScript 無効時用の元の脚注と ID を重複させない。
    copy.querySelectorAll("[id]").forEach(element => element.removeAttribute("id"))
    copy.querySelectorAll("a.reversefootnote").forEach(element => element.remove())
    copy.querySelectorAll("a.footnote").forEach(element => element.setAttribute("role", "button"))
    content.replaceChildren(...copy.childNodes)
    this.active.source = note.innerHTML
    this.active.label.textContent = `脚注 [${this.active.number}]`
    this.active.panel.setAttribute("aria-label", this.active.label.textContent)
    this.active.backButton.hidden = this.active.history.length === 0
  }

  followNestedReference(reference) {
    if (!reference || !this.active?.panel.contains(reference)) return false
    const note = [...this.active.originNote.closest(".footnotes").querySelectorAll(":scope > ol > li[id]")]
      .find(note => reference.getAttribute("href") === `#${note.id}`)
    if (!note) return false

    this.active.history.push({ note: this.active.note, number: this.active.number })
    this.active.note = note
    this.active.number = reference.textContent.trim()
    this.updateContent()
    this.active.content.focus({ preventScroll: true })
    return true
  }

  close(focus = false) {
    if (!this.active) return
    const { reference, panel } = this.active
    if (this.originalAttributes.has(reference)) {
      reference.setAttribute("aria-expanded", "false")
      reference.removeAttribute("aria-controls")
    }
    panel.remove()
    this.active = null
    if (focus && reference.isConnected) reference.focus()
  }
}
